import React, { useEffect, useState } from "react";
import PaginatedTable from "../common/PaginatedTable";
import DTIPages from "../common/DTIPages";
import { View } from "@instructure/ui-view";
import { Text } from "@instructure/ui-text";
import DtiToolbar from "../common/DtiToolbar";
import DtiKpiGrid from "../common/DtiKpiGrid";
import DtiSectionHeading from "../common/DtiSectionHeading";
import DtiOverviewSkeleton from "../common/DtiSkeleton";
import { downloadWithAuth } from "../../../utils";
import {
  getJobStudentOnlineRate,
  getStudentOnlineRate,
  createJobStudentOnlineRate,
  exportStudentParticipationRate,
} from "../api/api";

export default function StudentOnlineRate({ iframe = false }) {
  const [data, setData] = useState([]);
  const [totalParticipation, setTotalParticipation] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const applyData = (nextData) => {
    setData(nextData);
    setTotalParticipation(nextData?.totalStudentOnlineParticipation ?? null);
  };

  useEffect(() => {
    let intervalId;
    const fetchData = async () => {
      setIsFetching(true);
      try {
        applyData(await getStudentOnlineRate());

        const job = await getJobStudentOnlineRate();
        if (["starting", "pending"].includes(job?.workflowState)) {
          setIsLoading(true);
          intervalId = setInterval(async () => {
            const jobStatus = await getJobStudentOnlineRate();
            if (jobStatus?.workflowState === "done") {
              setIsLoading(false);
              clearInterval(intervalId);
              setIsFetching(true);
              try {
                applyData(await getStudentOnlineRate());
              } finally {
                setIsFetching(false);
              }
            }
          }, 1000);
        }
      } finally {
        setIsFetching(false);
      }
    };
    fetchData();
    return () => clearInterval(intervalId);
  }, []);

  const handleJob = async () => {
    if (isLoading) return;
    setIsLoading(true);
    await createJobStudentOnlineRate();
    const intervalId = setInterval(async () => {
      const job = await getJobStudentOnlineRate();
      if (job?.workflowState === "done") {
        setIsLoading(false);
        clearInterval(intervalId);
        setIsFetching(true);
        try {
          applyData(await getStudentOnlineRate());
        } finally {
          setIsFetching(false);
        }
      }
    }, 1000);
  };

  const showSkeleton = isFetching || isLoading;

  const handleExport = async (event) => {
    event.preventDefault();
    if (isExporting) return;
    setIsExporting(true);
    try {
      await downloadWithAuth(
        exportStudentParticipationRate(),
        "6.4-luot-truy-cap-hoc-sinh.xlsx",
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "[LỖI] Xuất báo cáo lượt truy cập học sinh thất bại:",
        error && error.message ? error.message : error,
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (iframe) {
    return (
      <View as="div" margin="small" textAlign="center">
        <Text size="large">Bạn không được quyền truy cập</Text>
      </View>
    );
  }

  if (showSkeleton) {
    return <DtiOverviewSkeleton kpiCount={1} showExport />;
  }

  return (
    <>
      <DtiToolbar
        isLoading={isLoading}
        onRefresh={handleJob}
        onExport={handleExport}
        isExporting={isExporting}
        exportLabel="Tải báo cáo Excel"
      />

      <DTIPages
        secondPage={
          <>
            <DtiSectionHeading>Chi tiết học sinh tham gia hoạt động trực tuyến</DtiSectionHeading>
            <li style={{ marginLeft: "20px" }}>
              Số học sinh: {data?.totalStudentOnlineParticipation}
            </li>
            <li style={{ marginLeft: "20px" }}>Thống kê chi tiết:</li>
            <PaginatedTable
              headers={[
                { id: "name", text: "Tê học sinh", width: "50%" },
                { id: "count", text: "Số lần phát sinh hoạt động học tập", width: "50%" },
              ]}
              rows={data?.dataStudentOnlineParticipation}
              perPage={10}
            />
          </>
        }
        firstPage={
          <DtiKpiGrid
            items={[
              {
                id: "totalStudentOnlineParticipation",
                label: "Học sinh phát sinh hoạt động học tập trực tuyến",
                value: totalParticipation,
              },
              {
                id: "totalStudents",
                label: "Tổng số học sinh",
                value: data?.totalStudents,
              },
            ]}
          />
        }
      />
    </>
  );
}
